import { useEffect, useRef, useState } from 'react'

import { EMPTY_STRING } from '../../../../constants/common'
import { SAVE_DRAFT_TIMEOUT } from '../../../../constants/draft'
import { EVENT_NAME_PAGE_HIDE } from '../../../../constants/event'
import { useThrottledState, useUserData } from '../../../../hooks'

import { GRETA_NEW_CHAT_DRAFT_KEY } from './useGretaAIChatDrafts.constants'
import {
  getGretaAIChatDraftText,
  readGretaAIChatUserDrafts,
  removeGretaAIChatDraft,
  upsertGretaAIChatDraft,
  writeGretaAIChatUserDrafts,
} from './useGretaAIChatDrafts.utils'

export function useGretaAIChatDrafts(activeChatId) {
  const { directory_id: userId } = useUserData()

  const draftKey = activeChatId ?? GRETA_NEW_CHAT_DRAFT_KEY

  const [draft, setDraftState] = useState(() =>
    getGretaAIChatDraftText(readGretaAIChatUserDrafts(userId), draftKey)
  )

  const draftRef = useRef(draft)
  const draftKeyRef = useRef(draftKey)

  useEffect(() => {
    draftRef.current = draft
    draftKeyRef.current = draftKey
  }, [draft, draftKey])

  function persistDraft({ key, text }) {
    writeGretaAIChatUserDrafts(
      userId,
      upsertGretaAIChatDraft(
        readGretaAIChatUserDrafts(userId),
        key,
        text,
        Date.now()
      )
    )
  }

  const [schedulePersist, getScheduledPersist, cancelPersist] =
    useThrottledState(persistDraft)

  const [loadedKey, setLoadedKey] = useState(draftKey)

  if (loadedKey !== draftKey) {
    setLoadedKey(draftKey)
    setDraftState(
      getGretaAIChatDraftText(readGretaAIChatUserDrafts(userId), draftKey)
    )
  }

  // Page hide flushes the throttled draft too, so a reload can't drop it.
  useEffect(() => {
    function flushScheduledDraft() {
      const scheduled = getScheduledPersist()

      if (scheduled !== undefined) {
        schedulePersist(scheduled, 0)
      }
    }

    window.addEventListener(EVENT_NAME_PAGE_HIDE, flushScheduledDraft)

    return () => {
      window.removeEventListener(EVENT_NAME_PAGE_HIDE, flushScheduledDraft)
      flushScheduledDraft()
    }
  }, [draftKey])

  function setDraft(text) {
    setDraftState(text)
    schedulePersist({ key: draftKey, text }, SAVE_DRAFT_TIMEOUT)
  }

  function discardActiveDraft() {
    setDraftState(EMPTY_STRING)
    cancelPersist()
    persistDraft({ key: draftKey, text: EMPTY_STRING })
  }

  // The active chat may have changed while the submit was pending (e.g. the
  // balance preflight) — clear the SUBMITTED chat's draft, not whatever is
  // currently displayed, and only if it still matches what was sent.
  function discardDraftIfSubmitted(chatId, text) {
    const key = chatId ?? GRETA_NEW_CHAT_DRAFT_KEY

    if (key === draftKeyRef.current) {
      if (draftRef.current === text) {
        discardActiveDraft()
      }

      return
    }

    const storedText = getGretaAIChatDraftText(
      readGretaAIChatUserDrafts(userId),
      key
    )

    if (storedText === text) {
      writeGretaAIChatUserDrafts(
        userId,
        removeGretaAIChatDraft(readGretaAIChatUserDrafts(userId), key)
      )
    }
  }

  function discardChatDraft(chatId) {
    const key = chatId ?? GRETA_NEW_CHAT_DRAFT_KEY

    if (key === draftKey) {
      discardActiveDraft()
    } else {
      writeGretaAIChatUserDrafts(
        userId,
        removeGretaAIChatDraft(readGretaAIChatUserDrafts(userId), key)
      )
    }
  }

  return {
    discardActiveDraft,
    discardChatDraft,
    discardDraftIfSubmitted,
    draft,
    setDraft,
  }
}
